import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Col, Row } from "react-styled-flexboxgrid";

import useFetchSubCategories from "./hooks/useFetchSubCategories";
import { TStepProps } from "./types";

import Box from "../../Global/Atomics/Box";
import { Button } from "../../Global/Atomics/Button";
import Input from "../../Global/Atomics/Input";
import LoadingNova from "../../Global/Atomics/LoadingNova";
import { Paragraph } from "../../Global/Atomics/Paragraph";
import SeveritySelector from "../../Global/Atomics/SeveritySelector";
import { SimpleDropdown } from "../../Global/Atomics/SimpleDropdown";
import TextArea from "../../Global/Atomics/TextArea";

import { useReportFormContext } from "../../../contexts/ReportFormContext";
import { useResponsiveContext } from "../../../contexts/ResponsiveContext";

type TFormValues = {
    title: string,
    description: string,
}

const Step2 = ({ step, setStep, resources }: TStepProps) => {
    const [viewMode] = useResponsiveContext();
    const { form, updateForm } = useReportFormContext();
    const { dropdownData, isLoading } = useFetchSubCategories(); // API Call to get dropdown data
    const { register, setValue, formState: { errors, isValid } } = useForm<TFormValues>({
        mode: 'onTouched',
        defaultValues: {
            [form.title.id]: '',
            [form.description.id]: '',
        }
    });

    const isMobile = viewMode === 'mobile';

    // Reset Form values after step change
    useEffect(() => {
        setValue(form.title.id, form.title.value, { shouldValidate: form.title.value ? true : false });
        setValue(form.description.id, form.description.value);
    }, [form.description.id, form.description.value, form.title.id, form.title.value, setValue]);


    if (isLoading) {
        return (
            <Box mt="lg" mb="sm" pb="xs">
                <LoadingNova />
            </Box>
        )
    }

    return (
        <>
            <Box my="lg">
                <Paragraph typography="text-s-bold" customColor="black-1">{resources.report.step2.title}</Paragraph>
            </Box>

            <Row>
                <Col xs={12} md={8}>
                    <Box mb="lg" alignItems="center">
                        <Input
                            value={form.title.value}
                            id={form.title.id}
                            required
                            type="text"
                            label={`${resources.form.report.title.label}`}
                            placeholder={`${resources.form.report.title.placeholder}*`}
                            errors={errors}
                            infoMessage={resources.form.report.title.infoMessage}
                            register={register(form.title.id, {
                                required: resources.form.report.title.validate.required,
                                onChange: (e) => { updateForm({ field: form.title.id, value: e.target.value }) },
                            })}
                        />
                    </Box>
                    <Box mb="lg" alignItems="center" pb="lg">
                        <TextArea 
                            id={form.description.id}
                            value={form.description.value}
                            rows={5}
                            maxLength={250}
                            required
                            placeholder={resources.form.report.description.placeholder}
                            errors={errors}
                            register={register(form.description.id, {
                                onChange: (e) => { updateForm({ field: form.description.id, value: e.target.value }) },
                            })}
                        />
                    </Box>
                    {dropdownData && (
                        <Box mb="lg" alignItems="center">
                            <SimpleDropdown
                                placeholder={form.dropdown.value.name ? form.dropdown.value.name : resources.form.report.dropdown.placeholder}
                                items={dropdownData}
                                handleSelection={(data) => updateForm({ field: form.dropdown.id, value: { name: data.value, id: data.id } })}
                            />
                        </Box>
                    )}
                    <Box mb="lg" alignItems="center">
                        <Paragraph typography="text-s-bold" customColor="black-1" pb="md" mb="xs">{resources.form.report.severity.label}</Paragraph>
                        <SeveritySelector
                            value={form.severity.value}
                            handleSelection={(value) => updateForm({ field: form.severity.id, value: value })}
                        />
                    </Box>
                </Col>
            </Row>

            <Row center={isMobile ? 'xs' : undefined}>
                <Col>
                    <Button
                        size="m"
                        variant="primary-black"
                        disabled={
                            !isValid ||                     // Ensure the form is valid
                            !form.title.value ||            // Title field must have a value
                            !form.description.value ||      // Description field must have a value
                            !form.dropdown.value.id ||      // Dropdown selection must be made
                            !form.severity.value            // Severity must be selected
                        }                        onClick={() => setStep(step + 1)}
                    >{resources.label.advance}
                    </Button>
                </Col>
            </Row>

        </>
    )
}

export default React.memo(Step2)